import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, FileBadge2, ShieldCheck, Sparkles, Workflow, Building2 } from "lucide-react";
import MarketingLayout from "@/components/public/MarketingLayout";
import { Button } from "@/components/ui/button";
import { faqItems, homeHighlights, workflowSteps } from "@/content/siteContent";

export default function HomePage() {
  return (
    <MarketingLayout>
      <section className="mx-auto max-w-7xl px-5 pb-20 pt-16 md:px-8 md:pt-24">
        <div className="grid items-center gap-14 lg:grid-cols-[1.1fr_0.9fr]">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.22em] text-slate-600 shadow-sm">
              <ShieldCheck className="h-3.5 w-3.5 text-sky-600" /> Piattaforma audit per consulenti e aziende
            </div>
            <h1 className="mt-6 max-w-3xl font-display text-5xl font-black leading-[0.95] text-slate-950 md:text-7xl">
              Trasforma audit e compliance in un servizio vendibile, scalabile e tracciabile.
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-600">
              Audit Pro unisce checklist multi-framework, gestione evidenze, versioning e report AI in un unico workspace web. Pensato per consulenti compliance, DPO e aziende che vogliono meno fogli Excel e piu controllo.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button asChild size="lg" className="h-12 rounded-full bg-[#0f172a] px-6 hover:bg-slate-800">
                <Link to="/pricing">Scopri i piani <ArrowRight className="h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="h-12 rounded-full border-slate-300 bg-white px-6">
                <Link to="/contact">Prenota una demo</Link>
              </Button>
            </div>
            <div className="mt-10 grid gap-4 text-sm text-slate-600 sm:grid-cols-3">
              <div className="rounded-2xl border border-slate-200 bg-white/80 p-4 shadow-sm">GDPR, NIS2, ISO 27001, DORA, AI Act</div>
              <div className="rounded-2xl border border-slate-200 bg-white/80 p-4 shadow-sm">Verbali AI, Word, PDF e branding white-label</div>
              <div className="rounded-2xl border border-slate-200 bg-white/80 p-4 shadow-sm">Consulenti e aziende nello stesso flusso</div>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-3 rounded-[32px] bg-gradient-to-br from-sky-400/20 via-white to-slate-900/10 blur-2xl" />
            <div className="relative overflow-hidden rounded-[32px] border border-slate-200 bg-[#0b1327] p-6 text-white shadow-2xl shadow-slate-900/10">
              <div className="flex items-center justify-between border-b border-white/10 pb-4">
                <div>
                  <div className="text-xs uppercase tracking-[0.25em] text-sky-200/80">Live workflow</div>
                  <div className="mt-1 font-display text-2xl font-black">Audit workspace</div>
                </div>
                <div className="rounded-full bg-emerald-400/10 px-3 py-1 text-xs text-emerald-200">Tenant attivo</div>
              </div>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <div className="rounded-2xl bg-white/5 p-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-white"><Workflow className="h-4 w-4 text-sky-300" /> Checklist multi-framework</div>
                  <p className="mt-2 text-sm leading-relaxed text-slate-300">Framework combinati, item pesati, versioni storicizzate e gap immediatamente leggibili.</p>
                </div>
                <div className="rounded-2xl bg-white/5 p-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-white"><Sparkles className="h-4 w-4 text-sky-300" /> Verbale DPO AI</div>
                  <p className="mt-2 text-sm leading-relaxed text-slate-300">Executive summary, remediation plan e output pronti per il cliente finale.</p>
                </div>
                <div className="rounded-2xl bg-white/5 p-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-white"><Building2 className="h-4 w-4 text-sky-300" /> Aziende e consulenti</div>
                  <p className="mt-2 text-sm leading-relaxed text-slate-300">Richieste accesso, approvazioni e governance centralizzata per tenant.</p>
                </div>
                <div className="rounded-2xl bg-white/5 p-4">
                  <div className="flex items-center gap-2 text-sm font-semibold text-white"><FileBadge2 className="h-4 w-4 text-sky-300" /> Evidenze documentali</div>
                  <p className="mt-2 text-sm leading-relaxed text-slate-300">PDF, Word, Excel e immagini allegate direttamente ai punti di controllo.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-20 md:px-8">
        <div className="mb-10 max-w-2xl">
          <div className="text-xs font-semibold uppercase tracking-[0.25em] text-sky-700">Perche funziona</div>
          <h2 className="mt-3 font-display text-4xl font-black text-slate-950">Un sito che vende il servizio, una piattaforma che lo consegna.</h2>
        </div>
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          {homeHighlights.map((item) => (
            <div key={item.title} className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm shadow-slate-200/40">
              <CheckCircle2 className="h-5 w-5 text-sky-600" />
              <h3 className="mt-4 font-display text-2xl font-bold text-slate-950">{item.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{item.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="border-y border-slate-200 bg-white/90">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 py-20 md:grid-cols-[0.9fr_1.1fr] md:px-8">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.25em] text-sky-700">Come lavora il software</div>
            <h2 className="mt-3 font-display text-4xl font-black text-slate-950">Dalla checklist al verbale finale senza cambiare strumento.</h2>
          </div>
          <div className="space-y-4">
            {workflowSteps.map((step, idx) => (
              <div key={step} className="flex gap-4 rounded-3xl border border-slate-200 bg-slate-50 px-5 py-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#0f172a] text-sm font-bold text-white">0{idx + 1}</div>
                <div className="pt-2 text-sm leading-relaxed text-slate-700">{step}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-20 md:px-8">
        <div className="grid gap-6 lg:grid-cols-[1fr_1fr]">
          {faqItems.map((item) => (
            <div key={item.q} className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="font-display text-2xl font-bold text-slate-950">{item.q}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{item.a}</p>
            </div>
          ))}
        </div>
      </section>
    </MarketingLayout>
  );
}
